import {isSafeUrl as isSafeUrlWithPie} from '@pie/naver-interfaces'

import {isEmpty, isString, validURL} from '@shared/helpers/lang'
import {omit} from '@shared/helpers/object'
import {qs} from '@shared/helpers/queryString'

export const isSafeUrl = (url: string) => {
    try {
        return isSafeUrlWithPie(url)
    } catch {
        return false
    }
}

interface ParseUrlParam {
    query: {[key: string]: string}
    key: string
}

type ParseUrlResult = {
    url: string
    isNaverDomain: boolean
}

export const parseUrlFromQuery = ({query, key}: ParseUrlParam): ParseUrlResult => {
    if (key in query) {
        const url = query[key] ?? ''

        return {
            url,
            isNaverDomain: isSafeUrl(url),
        }
    }

    return {
        url: '',
        isNaverDomain: false,
    }
}

export const isQueryParamExits = (url: string, params: string[]) => {
    const [, search = ''] = url.split('?')
    const query = qs.parse(search)

    return params.every((keyParam) => {
        return !isEmpty(query[keyParam])
    })
}

export const extractQueryFromUrl = (url: string, paramKeys: string[]) => {
    const [origin, search = ''] = url.split('?')
    const query = qs.parse(search)

    const restQuery = omit(paramKeys, query)

    const cleanUrl = `${origin}?${qs.stringify(restQuery)}`

    const queries: {[key: string]: string} = paramKeys.reduce((acc, key) => {
        Object.assign(acc, {
            [key]: query[key],
        })
        return acc
    }, {})

    return {
        cleanUrl,
        queries,
    }
}

/**
 * ?나 & 같은 쿼리스트링 예약어가 들어와도 string으로 취급해 url로 조합하는 유틸
 * 가맹점에서 잘못 제출하는 url도 그대로 제출하도록 처리한다
 * @param url
 * @param params
 */
export const urlMergedWithUnverifiedUrlParam = (url: string, params: {[key: string]: string | undefined}) => {
    // qs 사용시 특수문자 자동 encode되므로 순수 string으로 파라메터 작성
    const injectedQueryStr = Object.entries(params)
        .reduce((resultStr, [key, value]) => {
            // 값이 빈 스트링이면 아예 querystring에 추가하지 않는다
            if (value?.length) {
                return `${resultStr}&${key}=${value}`
            }
            return resultStr
        }, '')
        .substring(1) // 문자열 0번째 캐릭터 '&' 제거

    // url에 ?가 있으면 &으로 연결, 없으면 ?으로 쿼리 시작
    const connector = url.indexOf('?') > -1 ? '&' : '?'

    // injectedQueryStr 에 무언가 있으면 쿼리 문자열. 없으면 빈 문자열.
    const queryStrOrEmptyStr = injectedQueryStr ? `${connector}${injectedQueryStr}` : ''

    // 원본 url값 그대로 유지하고 추가 파라메터는 뒤에 추가한다
    return `${url}${queryStrOrEmptyStr}`
}

/** 디코드후 유효환 url 만 반환 */
export const decodeUrl = (encodedUrl?: string): string => {
    try {
        const url = encodedUrl ? decodeURIComponent(encodedUrl) : encodedUrl
        // IE11 지원
        if (typeof window.URL !== 'function') {
            if (url && validURL(url)) {
                return url
            }
        }
        if (url && Boolean(new URL(url))) {
            return url
        }
        return ''
    } catch {
        return ''
    }
}

export const getUrlInfo = ({url, addQuery, removeQuery}: {url: string; addQuery?: object; removeQuery?: string[]}) => {
    const {origin, pathname, search} = new URL(url)

    let resultQuery = qs.parse(search) ?? {}

    if (addQuery) {
        resultQuery = {
            ...resultQuery,
            ...addQuery,
        }
    }

    if (removeQuery) {
        resultQuery = omit(removeQuery, resultQuery)
    }

    const path = origin + (pathname === '/' ? '' : pathname)
    const qsSearch = qs.stringify(resultQuery)
    return {
        url: `${path}${qsSearch ? '?' + qsSearch : ''}`,
        path,
        pathWithQuery: `${pathname}${search}`,
        query: resultQuery,
    }
}

export const isUrlValid = (url: string): boolean => {
    try {
        // eslint-disable-next-line no-new
        new URL(url)
        return true
    } catch {
        return false
    }
}
export const examineUrlSafety = (url: Maybe<string | undefined>, fallbackUrl: string) => {
    try {
        if (url && isString(url)) {
            const {protocol} = new URL(url)
            const blacklist = ['javascript:']
            const unSafe = blacklist.includes(protocol.toLowerCase())

            if (unSafe) {
                return fallbackUrl
            } else {
                return url
            }
        } else {
            return fallbackUrl
        }
    } catch {
        return fallbackUrl
    }
}

/** 경로의 끝에 있는 '/' 제거 */
export const removeTrailingPathSeparator = (path: string) => {
    return path.replace(/\/$/, '')
}

/**
 * origin + pathname 반환
 * @example https://www.naver.com/path/to/page?a=b#section => https://www.naver.com/path/to/page
 */
export const getOriginPathname = (url: string) => {
    try {
        const {origin, pathname} = new URL(url)

        return origin + removeTrailingPathSeparator(pathname)
    } catch {
        return ''
    }
}

export const CloseParam = {
    BACK: 'back',
    CLOSE: 'close',
} as const

export type CloseParamType = (typeof CloseParam)[keyof typeof CloseParam]

/**
 * 외부제공 페이지 쿼리 파라미터 관리 함수
 * @wiki https://oss.fin.navercorp.com/payment-fe/order-web/wiki/%EC%99%B8%EB%B6%80%EC%A0%9C%EA%B3%B5-%ED%8E%98%EC%9D%B4%EC%A7%80-%EC%BF%BC%EB%A6%AC-%EC%A0%95%EC%B1%85
 */
export const getCommonQuery = ({
    search,
}: {
    search: string
}): {
    closeType?: CloseParamType
} => {
    try {
        const {closeType} = qs.parse(search)

        return {
            ...(['back', 'close'].includes(closeType) ? {closeType} : {}),
        }
    } catch {
        return {}
    }
}
